import React from "react";
import classnames from "classnames";

import Placeholder, { Rect } from "components/Placeholder";

type Props = {
  chipLabels?: Array<string>;
  chips?: Array<{ label: string; value: string }>;
  currentCategorySlug?: string;
  isLoading?: boolean;
  legend: string;
  onSelectionChange: (value: string) => void;
};

const RadioChipTray: React.FC<Props> = ({
  chipLabels = [],
  chips = [],
  currentCategorySlug,
  isLoading = true,
  legend,
  onSelectionChange,
}) => {
  if (!chips.length && chipLabels.length) {
    chips = chipLabels.map((label) => {
      return { label: label, value: label };
    });
  }
  const [selectedValue, setSelectedValue] = React.useState(
    chips?.length ? currentCategorySlug || chips[0].value : ""
  );

  const handleClick = (value: string) => {
    setSelectedValue(value);
    onSelectionChange(value);
  };

  if (!isLoading && !chips?.length) {
    return <></>;
  }

  return isLoading ? (
    <div>
      <div className="text-xs font-bold mb-xs">{legend}</div>
      <div className="flex lg:flex-wrap overflow-x-auto -mb-xs">
        {[...Array(3)].map((_e, i) => (
          <Placeholder
            width="84"
            height="26"
            key={`radio-chip-placeholder-${i}`}
            className="rounded-full mr-sm mb-xs"
          >
            <Rect y="0" width="100%" height="100%" />
          </Placeholder>
        ))}
      </div>
    </div>
  ) : (
    // Using aria-label instead of aria-labelledby to prevent id collision if multiple instances on page.
    <div role="radiogroup" aria-label={legend}>
      <div className="text-xs font-bold mb-xs">{legend}</div>
      <div className="flex lg:flex-wrap overflow-x-auto -mb-xs">
        {chips.map((chip) => (
          <button
            key={chip.label || chip.value}
            className={classnames(
              "flex-shrink-0 chip border border-light-grey mr-sm focus:outline-none mb-xs",
              {
                "bg-white text-green": chip.value !== selectedValue,
                "border-green-60": chip.value === selectedValue,
              }
            )}
            role="radio"
            onClick={() => handleClick(chip.value)}
            aria-checked={selectedValue === chip.value}
          >
            {chip.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default RadioChipTray;
